import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRightIcon, XIcon } from "lucide-react";
import type { RevealState } from "../hooks/useReveal";
import { Mandala } from "./Mandala";
interface RevealOverlayProps {
  title: string;
  subtitle: string;
  total: number;
  reveal: RevealState;
  envelopeKey: string;
  revealed: React.ReactNode;
  doneContent?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function RevealOverlay({
  title,
  subtitle,
  total,
  reveal,
  envelopeKey,
  revealed,
  doneContent,
  onClose,
  children
}: RevealOverlayProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const {
    current,
    open,
    isLast,
    allRevealed,
    revealedCount,
    next
  } = reveal;
  useEffect(() => {
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return <motion.div role="dialog" aria-modal="true" aria-labelledby="reveal-title" className="bg-waves fixed inset-0 z-50 flex flex-col overflow-y-auto text-white" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.22,
    ease
  }} data-id="element-317" data-mp="1fn7vx0marqpn">
      {/* Flag band: Brazil green & yellow meet India saffron & green */}
      <div className="flex h-1.5 shrink-0" aria-hidden="true" data-id="element-318" data-mp="1yjzef7u0m1sn">
        <span className="flex-1 bg-brgreen" data-id="element-319" data-mp="1tlpgipgwon3c" />
        <span className="flex-1 bg-bryellow" data-id="element-320" data-mp="1tmqryv1u3bll" />
        <span className="flex-1 bg-saffron" data-id="element-321" data-mp="4713cm0hr149" />
        <span className="flex-1 bg-ingreen" data-id="element-322" data-mp="47t0i19ql6wn" />
      </div>

      <header className="flex shrink-0 items-center justify-between gap-4 px-6 py-4 md:px-10" data-id="element-323" data-mp="3ve3w3hvz9fgl">
        <div data-id="element-324" data-mp="1zaph0lbap441">
          <h2 id="reveal-title" className="text-lg font-bold" data-id="element-325" data-mp="1zb7ko30aeklz">
            {title}
          </h2>
          <p className="text-sm text-white/60" data-id="element-326" data-mp="248qxcyhc6pbt">{subtitle}</p>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close reveal" className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors duration-150 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bryellow" data-id="element-327" data-mp="28b449q7tkta6">
          <XIcon className="h-5 w-5" data-id="element-328" data-mp="2n6a8q8r9ok1e" />
        </button>
      </header>

      <div className="grid flex-1 lg:grid-cols-[1fr_360px]" data-id="element-329" data-mp="2s0eewpspko1f">
        <section className="relative flex flex-col items-center justify-center overflow-hidden px-6 pb-10" data-id="element-330" data-mp="2w0gjp7tqpxkx">
          <Mandala className="pointer-events-none absolute left-1/2 top-1/2 h-[820px] w-[820px] -translate-x-1/2 -translate-y-[45%] text-bryellow/15" data-id="element-331" data-mp="310b916xvaf3v" />

          <div className="relative w-full max-w-[min(540px,calc((100vh-380px)*1.5))] pt-[240px]" data-id="element-332" data-mp="35wrxy3cw4xi0">
            <AnimatePresence mode="wait" data-id="element-333" data-mp="3g0q7f79zuk06">
              <motion.div key={envelopeKey} initial={{
              opacity: 0,
              scale: 0.96,
              y: 10
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.24,
              ease
            }} data-id="element-334" data-mp="3b36tk8h7k98r">
                {children}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative mt-8 flex min-h-[48px] w-full max-w-[540px] items-center justify-between gap-4" data-id="element-335" data-mp="3ttpnpbhloi8s">
            <p className="text-sm tabular-nums text-white/70" data-id="element-336" data-mp="3p2fz8e7sfkyh">
              Envelope {current + 1} of {total}
            </p>
            {!open && <p className="text-sm text-white/70" data-id="element-337" data-mp="3kzteex9jb0as">Click the envelope to open it</p>}
            {open && !isLast && <button type="button" onClick={next} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-bryellow px-5 py-3 text-sm font-bold text-navy transition-[transform,background-color] duration-150 ease-out hover:bg-[#ffd633] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy" data-id="element-338" data-mp="gnpjr04htsb2">
                Next envelope
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-339" data-mp="jw5wsw8bg1to" />
              </button>}
          </div>
        </section>

        <aside className="border-t border-white/10 bg-navy-deep/60 px-6 py-6 lg:border-l lg:border-t-0" data-id="element-340" data-mp="ou7hflle4979">
          <h3 className="pb-3 text-sm font-semibold" data-id="element-341" data-mp="b32nn04peb4u">
            Revealed{" "}
            <span className="font-normal text-white/60" data-id="element-342" data-mp="f4nxeclxy9at">
              {revealedCount}/{total}
            </span>
          </h3>
          {revealedCount > 0 ? revealed : <p className="text-sm text-white/60" data-id="element-343" data-mp="12t72ykdlw7z7">Opened envelopes will appear here.</p>}
          {allRevealed && <div className="mt-6 space-y-5" data-id="element-344" data-mp="2sbvxa3w01o35">
              {doneContent}
              <button type="button" onClick={onClose} className="block text-sm font-semibold text-bryellow transition-colors duration-150 hover:text-white" data-id="element-345" data-mp="2sa2nifq3anqx">
                Close
              </button>
            </div>}
        </aside>
      </div>
    </motion.div>;
}